/**
 * Individual represents an instance (ABox) in the knowledge graph.
 * This is used when creating instances from the toolbox or concept chips.
 *
 * Unlike SchemaClass which represents a concept/class (TBox), Individual represents
 * a specific instance that has a type (rdf:type) relationship to a SchemaClass.
 *
 * The referenced SchemaClass can come from a different OntologySchema (TBox/ABox separation).
 *
 * Implements the Individual interface from semantical-core for unified handling
 * with TBox individuals (from ontology-model).
 */

import Concept from "@semantical/modules/vocabulary/Concept.ts";
import Vocabulary from "@semantical/modules/vocabulary/Vocabulary.ts";
import type { Individual as IIndividual } from "@semantical/modules/semantical-core";
import SchemaClass from "@semantical/modules/ontology-model/SchemaClass.ts";

/**
 * Extract a readable label from a URI (last segment after # or /)
 */
function extractLabelFromUri(uri: string): string {
    const hashIndex = uri.lastIndexOf('#');
    if (hashIndex !== -1) return uri.substring(hashIndex + 1);
    const slashIndex = uri.lastIndexOf('/');
    if (slashIndex !== -1) return uri.substring(slashIndex + 1);
    return uri;
}

/**
 * Individual represents an instance (ABox) in the knowledge graph.
 * Implements the Individual interface from semantical-core.
 */
export default class Individual implements IIndividual {
    concept: Concept;

    /** The URI of this individual instance */
    individualUri: string;
    /** The display label for this individual */
    individualLabel: string;
    /** The SchemaClass this individual is an instance of (can be from another OntologySchema) */
    modelClass: SchemaClass;

    /**
     * Property values for this individual.
     * Key: property URI, Value: array of property values (supports multi-value properties)
     */
    propertyValues: Map<string, string[]> = new Map();

    constructor(concept: Concept, uri: string, label: string, modelClass: SchemaClass) {
        this.concept = concept;
        this.individualUri = uri;
        this.individualLabel = label;
        this.modelClass = modelClass;
    }

    /**
     * URIs of classes this individual is an instance of.
     * For ABox individuals, this is derived from the modelClass.
     */
    get classUris(): string[] {
        return [this.modelClass.uri];
    }

    /**
     * Check if this individual is an instance of a given class.
     * @param classUri The URI of the class to check
     */
    isInstanceOf(classUri: string): boolean {
        return this.modelClass.uri === classUri;
    }

    /**
     * Set a property value for this individual (replaces all existing values with single value).
     * @param propertyUri The URI of the data property
     * @param value The value to set, or null to delete all values
     */
    setPropertyValue(propertyUri: string, value: string | null): void {
        if (value === null || value === '') {
            this.propertyValues.delete(propertyUri);
        } else {
            this.propertyValues.set(propertyUri, [value]);
        }
    }

    /**
     * Get the first property value for this individual (for single-value properties).
     * @param propertyUri The URI of the data property
     * @returns The first value, or undefined if not set
     */
    getPropertyValue(propertyUri: string): string | undefined {
        const values = this.propertyValues.get(propertyUri);
        return values && values.length > 0 ? values[0] : undefined;
    }

    /**
     * Get all property values for this individual (for multi-value properties).
     * @param propertyUri The URI of the data property
     * @returns Array of values, or empty array if not set
     */
    getPropertyValues(propertyUri: string): string[] {
        return this.propertyValues.get(propertyUri) || [];
    }

    /**
     * Add a property value to this individual (for multi-value properties).
     * @param propertyUri The URI of the data property
     * @param value The value to add
     */
    addPropertyValue(propertyUri: string, value: string): void {
        if (!value) return;
        const existing = this.propertyValues.get(propertyUri) || [];
        if (!existing.includes(value)) {
            this.propertyValues.set(propertyUri, [...existing, value]);
        }
    }

    /**
     * Remove a specific property value from this individual.
     * @param propertyUri The URI of the data property
     * @param value The value to remove
     */
    removePropertyValue(propertyUri: string, value: string): void {
        const existing = this.propertyValues.get(propertyUri) || [];
        const filtered = existing.filter(v => v !== value);
        if (filtered.length === 0) {
            this.propertyValues.delete(propertyUri);
        } else {
            this.propertyValues.set(propertyUri, filtered);
        }
    }

    /**
     * Update a property value in place (keeps order).
     * @param propertyUri The URI of the data property
     * @param oldValue The value to replace
     * @param newValue The new value
     */
    updatePropertyValue(propertyUri: string, oldValue: string, newValue: string): void {
        const existing = this.propertyValues.get(propertyUri) || [];
        const index = existing.indexOf(oldValue);
        if (index !== -1) {
            existing[index] = newValue;
            this.propertyValues.set(propertyUri, existing);
        }
    }

    /**
     * Delete all property values for a property from this individual.
     * @param propertyUri The URI of the data property
     */
    deletePropertyValue(propertyUri: string): void {
        this.propertyValues.delete(propertyUri);
    }

    /**
     * Check if a property has any values set.
     * @param propertyUri The URI of the data property
     */
    hasPropertyValue(propertyUri: string): boolean {
        const values = this.propertyValues.get(propertyUri);
        return values !== undefined && values.length > 0;
    }

    /**
     * The display name of this individual (from the concept's prefLabel)
     */
    get name(): string {
        return this.concept.prefLabel;
    }

    /**
     * The definition of this individual (from the concept)
     */
    get definition(): string {
        return this.concept.definition;
    }

    /**
     * The identifying URI for this individual
     */
    get uri(): string {
        return this.individualUri;
    }

    /**
     * The stereotype to display (the class label, e.g., "Belgian Citizen")
     */
    get stereotype(): string {
        return this.modelClass.label || this.modelClass.name;
    }

    toJSON() {
        // Convert propertyValues Map to a plain object for serialization
        // Store as arrays to support multi-value properties
        const propertyValuesObj: Record<string, string[]> = {};
        this.propertyValues.forEach((values, key) => {
            propertyValuesObj[key] = values;
        });

        return {
            type: 'individual',
            concept: {
                href: this.concept.uri,
                prefLabel: this.concept.prefLabel,
                definition: this.concept.definition
            },
            // Individual properties (flattened, no longer nested in owlIndividual)
            individualUri: this.individualUri,
            individualLabel: this.individualLabel,
            // Reference the SchemaClass by URI - resolved via modelClassResolver
            modelClassRef: this.modelClass.uri,
            propertyValues: propertyValuesObj
        };
    }

    /**
     * Deserialize from JSON. Requires a modelClassResolver function to look up the SchemaClass
     * from a TBox OntologySchema by URI.
     */
    static fromJSON(
        json: any,
        vocabulary: Vocabulary,
        modelClassResolver?: (uri: string) => SchemaClass | undefined
    ): Individual {
        // Try to get concept from vocabulary, or create from stored data if not found
        let concept: Concept;
        if (vocabulary.hasConcept(json.concept.href)) {
            concept = vocabulary.getConcept(json.concept.href);
        } else {
            concept = new Concept(
                json.concept.href,
                json.concept.prefLabel || json.concept.href,
                [],
                [],
                json.concept.definition || ""
            );
        }

        // Resolve the SchemaClass from the TBox
        const classUri = json.modelClassRef;
        let modelClass: SchemaClass;

        if (modelClassResolver && classUri) {
            const resolved = modelClassResolver(classUri);
            if (resolved) {
                modelClass = resolved;
            } else {
                // Fallback: create minimal SchemaClass from stored data
                console.warn(`Individual.fromJSON: Could not resolve SchemaClass ${classUri}, using fallback`);
                const classLabel = extractLabelFromUri(classUri);
                const fallbackConcept = new Concept(classUri, classLabel, [], [], "");
                modelClass = new SchemaClass('class', fallbackConcept, { label: classLabel });
            }
        } else {
            // No resolver or no classUri - create minimal placeholder
            console.warn(`Individual.fromJSON: No SchemaClass reference for individual ${json.individualUri}`);
            const fallbackConcept = new Concept('unknown', 'Unknown', [], [], "");
            modelClass = new SchemaClass('class', fallbackConcept, { label: 'Unknown' });
        }

        const individual = new Individual(
            concept,
            json.individualUri,
            json.individualLabel,
            modelClass
        );

        // Restore property values from JSON
        // Supports both old format (string) and new format (string[])
        if (json.propertyValues) {
            for (const [uri, value] of Object.entries(json.propertyValues)) {
                if (Array.isArray(value)) {
                    // New format: array of strings
                    individual.propertyValues.set(uri, value as string[]);
                } else if (typeof value === 'string') {
                    // Old format: single string - convert to array
                    individual.propertyValues.set(uri, [value]);
                }
            }
        }

        return individual;
    }
}
